// src/pages/proyectos/components/FasesList.tsx
import { useState } from 'react';
import { Button, Badge, Spinner } from '@/components/ui';
import { Plus, GripVertical, Calendar, Clock, Flag, MoreVertical, Edit2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useFases, useEliminarFase } from '@/hooks/queries/useProyectos';
import { FaseFormModal } from './FaseFormModal';
import type { ProyectoFase } from '@/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';

const FASE_ESTADO_LABELS: Record<string, string> = {
  PENDIENTE: 'Pendiente',
  EN_PROGRESO: 'En Progreso',
  COMPLETADA: 'Completada',
  CANCELADA: 'Cancelada',
};

const FASE_ESTADO_COLORS: Record<string, string> = {
  PENDIENTE: 'bg-gray-100 text-gray-800',
  EN_PROGRESO: 'bg-blue-100 text-blue-800',
  COMPLETADA: 'bg-green-100 text-green-800',
  CANCELADA: 'bg-red-100 text-red-800',
};

interface Props {
  proyectoId: string;
}

export function FasesList({ proyectoId }: Props) {
  const { data: fases, isLoading } = useFases(proyectoId);
  const eliminarMutation = useEliminarFase();
  const [modalOpen, setModalOpen] = useState(false);
  const [faseToEdit, setFaseToEdit] = useState<ProyectoFase | undefined>();

  const handleEdit = (fase: ProyectoFase) => {
    setFaseToEdit(fase);
    setModalOpen(true);
  };

  const handleDelete = async (fase: ProyectoFase) => {
    if (confirm(`¿Eliminar la fase "${fase.nombre}"?`)) {
      await eliminarMutation.mutateAsync({ faseId: fase.id, proyectoId });
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setFaseToEdit(undefined);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium">Fases del proyecto</h3>
        <Button size="sm" onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Nueva Fase
        </Button>
      </div>

      {!fases || fases.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No hay fases definidas. Crea la primera fase del proyecto.
        </div>
      ) : (
        <div className="space-y-3">
          {fases.map((fase) => (
            <div
              key={fase.id}
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{fase.nombre}</span>
                  {fase.es_hito && (
                    <Flag className="h-4 w-4 text-orange-500" />
                  )}
                  <Badge className={FASE_ESTADO_COLORS[fase.estado] ?? 'bg-gray-100 text-gray-800'}>
                    {FASE_ESTADO_LABELS[fase.estado] ?? fase.estado}
                  </Badge>
                </div>
                {fase.descripcion && (
                  <p className="text-sm text-gray-500 mt-1 truncate">{fase.descripcion}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  {fase.fecha_inicio_planeada && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(fase.fecha_inicio_planeada), 'dd MMM', { locale: es })}
                      {fase.fecha_fin_planeada && (
                        <> - {format(new Date(fase.fecha_fin_planeada), 'dd MMM', { locale: es })}</>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {fase.horas_registradas}h
                    {fase.horas_estimadas && <> / {fase.horas_estimadas}h</>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {fase.actividades_count > 0 && (
                  <span className="text-xs text-gray-500">
                    {fase.actividades_completadas_count}/{fase.actividades_count} tareas
                  </span>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(fase)}>
                      <Edit2 className="h-4 w-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDelete(fase)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}

      <FaseFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        proyectoId={proyectoId}
        fase={faseToEdit}
      />
    </div>
  );
}
